import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { LoginRequest, LoginResponse, ApiResponse } from '@/api/types/auth.types';

export interface LoginResult {
  mfa_required: true;
  mfa_session_token: string;
}

type ApiErrorWithCode = Error & { code?: string };

function toApiError(errors: ApiResponse<unknown>['errors'], fallback: string): ApiErrorWithCode {
  const apiErr = errors?.[0];
  const e = new Error(apiErr?.message ?? fallback) as ApiErrorWithCode;
  if (apiErr?.code) e.code = apiErr.code;
  return e;
}

export interface EnrollmentRequiredData {
  mfaEnrollmentRequired: true;
  setupToken: string;
  expiresAt: string;
}

export function useLogin(
  onMfaRequired: (token: string) => void,
  onReverificationRequired: (email: string) => void,
  onEnrollmentRequired?: (data: EnrollmentRequiredData) => void,
  onErrorMessage?: (message: string) => void,
) {
  const { login } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (req: LoginRequest) => {
      const { data } = await apiClient.post<ApiResponse<LoginResponse | LoginResult>>('/auth/login', req);
      if (!data.data) throw toApiError(data.errors, 'Login failed');
      return data.data;
    },
    onSuccess: (data) => {
      if ('mfa_required' in data && data.mfa_required) {
        onMfaRequired(data.mfa_session_token);
        return;
      }
      const d = data as LoginResponse;
      login(d.accessToken, {
        ...d.user,
        tenantId: '',
        lastLoginAt: null,
      });
      apiClient.get('/auth/me').then((res) => {
        const user = res.data.data;
        useAuthStore.setState({ user, tenantId: user.tenantId, entitledModules: user.entitledModules ?? [] });
      });
      navigate('/dashboard');
    },
    onError: (
      err: ApiErrorWithCode & {
        response?: { data?: { data?: EnrollmentRequiredData; errors?: ApiResponse<unknown>['errors'] } };
      },
      variables: LoginRequest,
    ) => {
      // 403 enrollment gate — server sends data.data.mfaEnrollmentRequired = true
      const enrollData = err.response?.data?.data;
      if (enrollData?.mfaEnrollmentRequired) {
        onEnrollmentRequired?.(enrollData);
        return;
      }
      // Reverification gate — server returns a 401 with errors[0].code === 'reverification_required'.
      // A 401 rejects inside axios before toApiError runs, so the code lives on the nested response
      // body, not on err.code. Read the same nested shape the enrollment branch above uses.
      if (err.response?.data?.errors?.[0]?.code === 'reverification_required') {
        onReverificationRequired(variables.email);
        return;
      }
      const message = err.response?.data?.errors?.[0]?.message || 'Invalid credentials';
      if (onErrorMessage) onErrorMessage(message);
      else toast.error(message);
    },
  });
}

export function useMfaEnrollmentStart() {
  return useMutation({
    mutationFn: async (setupToken: string) => {
      const { data } = await apiClient.post('/auth/mfa/enrollment/start', { setupToken });
      if (!data.data) throw new Error('Enrollment start failed');
      return data.data as { secret: string; otpauthUrl: string };
    },
  });
}

export function useMfaEnrollmentConfirm() {
  const { login } = useAuthStore();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (body: { setupToken: string; totpCode: string }) => {
      const { data } = await apiClient.post('/auth/mfa/enrollment/confirm', body);
      if (!data.data) throw new Error('Enrollment confirmation failed');
      return data.data as LoginResponse;
    },
    onSuccess: (data) => {
      login(data.accessToken, {
        ...data.user,
        tenantId: '',
        lastLoginAt: null,
      });
      apiClient.get('/auth/me').then((res) => {
        const user = res.data.data;
        useAuthStore.setState({ user, tenantId: user.tenantId, entitledModules: user.entitledModules ?? [] });
      });
      navigate('/dashboard');
    },
  });
}

export function useMfaChallenge(
  onReverificationRequired?: () => void,
  onErrorMessage?: (message: string) => void,
) {
  const { login } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (body: { mfa_session_token: string; totp_code: string }) => {
      const { data } = await apiClient.post<ApiResponse<LoginResponse>>('/auth/mfa/challenge', body);
      if (!data.data) throw toApiError(data.errors, 'Invalid code');
      return data.data;
    },
    onSuccess: (data) => {
      login(data.accessToken, {
        ...data.user,
        tenantId: '',
        lastLoginAt: null,
      });
      apiClient.get('/auth/me').then((res) => {
        const user = res.data.data;
        useAuthStore.setState({ user, tenantId: user.tenantId, entitledModules: user.entitledModules ?? [] });
      });
      navigate('/dashboard');
    },
    onError: (
      err: ApiErrorWithCode & { response?: { data?: { errors?: ApiResponse<unknown>['errors'] } } },
    ) => {
      // Reverification gate on the MFA-challenge path — same nested 401 shape as the login
      // path (errors[0].code === 'reverification_required'). Read it off the nested response
      // body, not err.code (which on a 401 is axios's ERR_BAD_REQUEST). Mirrors the useLogin fix.
      if (err.response?.data?.errors?.[0]?.code === 'reverification_required' && onReverificationRequired) {
        onReverificationRequired();
        return;
      }
      const message = err.response?.data?.errors?.[0]?.message || 'Invalid authenticator code';
      if (onErrorMessage) onErrorMessage(message);
      else toast.error(message);
    },
  });
}

export function useLogout() {
  const { logout, accessToken } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      if (accessToken) {
        await apiClient.post('/auth/logout').catch(() => {});
      }
    },
    onSettled: () => {
      logout();
      navigate('/login');
    },
  });
}
